import { addMinutes, format, isValid, parse, startOfDay } from "date-fns";

/** PostgreSQL TIME / API canonical format (hours 00–23). */
export const TIME_HH_MM_SS_REGEX = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;

/** Anchor date for TIME-only parsing/formatting (consistent with NightHoursDisplay). */
const TIME_ANCHOR = new Date(2026, 2, 11);

const TIME_PARSE_FORMATS = ["HH:mm:ss", "HH:mm", "H:mm"] as const;

const MAX_MINUTES_SINCE_MIDNIGHT = 23 * 60 + 59;

const prepareTimeInput = (value: string): string =>
  value
    .trim()
    .replace(/h/i, ":")
    .replace(/(\d{1,2}:\d{2}:\d{2})\.\d+/, "$1");

const parseTimeStringToDate = (value: string): Date | null => {
  const preparedValue = prepareTimeInput(value);

  for (const timeFormat of TIME_PARSE_FORMATS) {
    const parsedDate = parse(preparedValue, timeFormat, TIME_ANCHOR);
    if (!isValid(parsedDate)) continue;

    const hours = parsedDate.getHours();
    const minutes = parsedDate.getMinutes();
    if (hours > 23 || minutes > 59) return null;

    // Reject values that roll over to the next day (e.g. 24:00).
    if (parsedDate.getDate() !== TIME_ANCHOR.getDate()) return null;

    return parsedDate;
  }

  return null;
};

/**
 * Parses a UI or API time string (HH:mm, HH:mm:ss, optional fractional seconds, or HHhmm) to minutes since midnight.
 * Returns null when the value cannot be parsed or is out of range (e.g. 24:00).
 */
export const parseTimeStringToMinutes = (value: string | null | undefined): number | null => {
  if (value == null || value.trim() === "") return null;

  const parsedDate = parseTimeStringToDate(value);
  if (!parsedDate) return null;

  return parsedDate.getHours() * 60 + parsedDate.getMinutes();
};

/** Converts minutes since midnight to API TIME string (HH:mm:ss). */
export const formatMinutesToTimeHHmmss = (minutes: number): string => {
  const clampedMinutes = Math.min(Math.max(0, Math.floor(minutes)), MAX_MINUTES_SINCE_MIDNIGHT);
  const date = addMinutes(startOfDay(TIME_ANCHOR), clampedMinutes);
  return format(date, "HH:mm:ss");
};

/**
 * Normalizes a time string to HH:mm:ss for API/DB storage.
 * Accepts HH:mm, HH:mm:ss, fractional seconds from PostgreSQL, and HHhmm display format.
 */
export const normalizeTimeToHHmmss = (value: string | null | undefined): string | null => {
  const minutes = parseTimeStringToMinutes(value);
  if (minutes === null) return null;
  return formatMinutesToTimeHHmmss(minutes);
};

export const isValidTimeHHmmss = (value: string): boolean => TIME_HH_MM_SS_REGEX.test(value);

/** Formats Postgres TIME / HH:mm:ss (or HH:mm) as HH:mm for UI fields. */
export const formatTimeHHmmssForDisplay = (raw: string | undefined | null): string => {
  const normalized = normalizeTimeToHHmmss(raw);
  if (!normalized) {
    return "";
  }
  return normalized.slice(0, 5);
};
